.faq-type-2 {
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;

  &__header {
    text-align: center;
    width: 100%;
    max-width: $breakpoint-desktop;
    margin: 0 auto var(--spacing-4xl);

    @include desktop {
      margin-bottom: var(--spacing-5xl);
    }
  }

  &__footer {
    text-align: center;
    width: 100%;
    max-width: $breakpoint-desktop;
    margin: var(--spacing-4xl) auto 0;

    @include desktop {
      margin-top: var(--spacing-5xl);
    }
  }

  &--align-start {
    align-items: flex-start;

    .faq-type-2__header {
      text-align: start;
      margin-left: 0;
      margin-right: 0;
    }

    .faq-type-2__footer {
      text-align: start;
      margin-left: 0;
      margin-right: 0;
    }

    .faq-type-2__accordion {
      max-width: 100%;
    }
  }

  &__accordion {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    align-self: stretch;
    width: 100%;
    max-width: $breakpoint-desktop;
    margin: 0 auto;
    gap: var(--spacing-md);

    &--two-columns {
      max-width: 100%;
    }

    &--animated {
      .faq-type-2__item-content {
        transition: height 0.35s cubic-bezier(0.4, 0, 0.2, 1);

        @media (prefers-reduced-motion: reduce) {
          transition-duration: 0.01ms;
        }
      }

      .faq-type-2__item-icon::before,
      .faq-type-2__item-icon::after {
        transition:
          transform 0.3s ease,
          opacity 0.25s ease;
      }
    }
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    position: relative;
    border: var(--border-size-sm) solid var(--border-color-primary);
    padding: var(--spacing-lg);

    @include desktop {
      padding: var(--spacing-xl);
    }

    &:focus-within:has(*:focus-visible) {
      outline: var(--border-size-md) solid var(--color-brand-primary-light) !important;
      outline-offset: var(--border-size-md);
      border-radius: var(--radius-size-sm);
      z-index: 1;
    }

    &-header {
      width: 100%;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      align-items: center;
      text-align: left;

      &:focus {
        outline: none;
      }

      // Real <h3> for SEO; display:contents keeps the me-text title as the
      // effective flex child so the layout is unchanged.
      .faq-type-2__item-title-heading {
        display: contents;
      }

      .faq-type-2__item-title {
        @include text-line-clamp(2);

        overflow: hidden;
        font-size: map-get($font-sizes, md);
        line-height: map-get($line-heights, md);
        font-weight: map-get($font-weights, bold);

        @include desktop {
          font-size: map-get($font-sizes, lg);
          line-height: map-get($line-heights, lg);
        }
      }
    }

    // CSS icon: + (closed) → − (open)
    &-icon {
      position: relative;
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      color: var(--color-neutral-black);

      &::before,
      &::after {
        content: '';
        position: absolute;
        width: 16px;
        height: 1.5px;
        background-color: currentcolor;
        top: 50%;
        left: 50%;
      }

      &::before {
        transform: translate(-50%, -50%) rotate(0deg);
      }

      &::after {
        transform: translate(-50%, -50%) rotate(90deg);
        opacity: 1;
      }
    }

    // The height is driven from index.tsx (measured px -> `auto` on
    // transitionend). The CSS-only `grid-template-rows: 0fr -> 1fr` trick was
    // dropped because iOS Safari resolves the open row shorter than the content,
    // which clipped long answers on real devices.
    &-content {
      width: 100%;
      height: 0;
      overflow: hidden;
    }

    // Padding lives on the inner wrapper unconditionally: it is clipped while
    // the panel is collapsed and is part of `scrollHeight`, so the measured
    // target height always matches the final layout.
    &-content-inner {
      font-size: map-get($font-sizes, md);
      line-height: map-get($line-heights, md);
      font-weight: map-get($font-weights, normal);
      padding-top: var(--spacing-lg);

      @include desktop {
        padding-top: var(--spacing-xl);
      }

      > :last-child {
        margin-bottom: 0;
      }
    }

    &--open {
      // − state: vertical arm rotates to horizontal and fades out
      .faq-type-2__item-icon::after {
        transform: translate(-50%, -50%) rotate(0deg);
        opacity: 0;
      }

      // Pre-hydration / no-JS fallback so a server-rendered open item is
      // already expanded. Once the component renders, the inline height set in
      // index.tsx wins over this rule and drives the animation.
      .faq-type-2__item-content {
        height: auto;
      }
    }
  }
}

// When JS is disabled all FAQ answers must appear expanded.
@media (scripting: none) {
  .faq-type-2__item-content {
    height: auto;
    overflow: visible;
    transition: none;
  }
}
